import React from 'react';
import { useResponsive } from '../hooks/useResponsive';

/**
 * TeamAggregationStats Component
 *
 * Displays team-level aggregation statistics for admin view.
 * Shows assignment counts, completion rates, active coaches, and last activity per team.
 *
 * @param {Array} assignments - All org-wide assignments
 * @param {Array} teams - All teams in the organization
 */
const TeamAggregationStats = ({ assignments = [], teams = [] }) => {
  const { isDesktop } = useResponsive();

  // Calculate statistics per team
  const teamStats = teams.map((team) => {
    const teamAssignments = assignments.filter((a) => a.player?.team_id === team.id);

    const completedAssignments = teamAssignments.filter((a) => a.completed);
    const completionRate =
      teamAssignments.length > 0
        ? Math.round((completedAssignments.length / teamAssignments.length) * 100)
        : 0;

    // Get unique coaches who have assigned drills to this team
    const activeCoaches = new Set(teamAssignments.map((a) => a.assigned_by).filter(Boolean)).size;

    // Get last assignment date
    const lastActivity =
      teamAssignments.length > 0
        ? Math.max(...teamAssignments.map((a) => new Date(a.assigned_date).getTime()))
        : null;

    return {
      team,
      totalAssignments: teamAssignments.length,
      completedAssignments: completedAssignments.length,
      completionRate,
      activeCoaches,
      lastActivity,
    };
  });

  const styles = {
    container: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      padding: isDesktop ? '24px' : '16px',
      marginBottom: isDesktop ? '20px' : '12px',
    },
    title: {
      fontSize: isDesktop ? '20px' : '18px',
      fontWeight: 'bold',
      marginBottom: '16px',
      color: '#333',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: isDesktop ? 'repeat(auto-fill, minmax(280px, 1fr))' : '1fr',
      gap: isDesktop ? '16px' : '12px',
    },
    card: {
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      padding: '16px',
      backgroundColor: '#fafafa',
    },
    inactiveCard: {
      border: '1px solid #ffcc00',
      borderRadius: '8px',
      padding: '16px',
      backgroundColor: '#fff9e6',
    },
    teamName: {
      fontSize: '16px',
      fontWeight: 'bold',
      marginBottom: '12px',
      color: '#333',
    },
    stat: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '8px',
    },
    statLabel: {
      fontSize: '14px',
      color: '#666',
    },
    statValue: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#333',
    },
    completionRate: {
      fontSize: '14px',
      fontWeight: '600',
    },
    warning: {
      fontSize: '12px',
      color: '#e65100',
      fontWeight: '500',
      marginTop: '8px',
      padding: '6px',
      backgroundColor: '#fff3e0',
      borderRadius: '4px',
      textAlign: 'center',
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px 20px',
      color: '#999',
      fontSize: '14px',
    },
  };

  const getCompletionColor = (rate) => {
    if (rate >= 80) return '#388e3c'; // Green
    if (rate >= 50) return '#f57c00'; // Orange
    return '#d32f2f'; // Red
  };

  const formatLastActivity = (timestamp) => {
    if (!timestamp) return 'No activity';

    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  if (teams.length === 0) {
    return (
      <div style={styles.container}>
        <h3 style={styles.title}>Team Overview</h3>
        <div style={styles.emptyState}>No teams found in this organization.</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Team Overview</h3>
      <div style={styles.grid}>
        {teamStats.map(
          ({
            team,
            totalAssignments,
            completedAssignments,
            completionRate,
            activeCoaches,
            lastActivity,
          }) => {
            const isInactive = totalAssignments === 0;

            return (
              <div key={team.id} style={isInactive ? styles.inactiveCard : styles.card}>
                <div style={styles.teamName}>{team.name}</div>

                <div style={styles.stat}>
                  <span style={styles.statLabel}>Total Assignments:</span>
                  <span style={styles.statValue}>{totalAssignments}</span>
                </div>

                <div style={styles.stat}>
                  <span style={styles.statLabel}>Completed:</span>
                  <span style={styles.statValue}>{completedAssignments}</span>
                </div>

                <div style={styles.stat}>
                  <span style={styles.statLabel}>Completion Rate:</span>
                  <span
                    style={{
                      ...styles.completionRate,
                      color: getCompletionColor(completionRate),
                    }}
                  >
                    {completionRate}%
                  </span>
                </div>

                <div style={styles.stat}>
                  <span style={styles.statLabel}>Active Coaches:</span>
                  <span style={styles.statValue}>{activeCoaches}</span>
                </div>

                <div style={styles.stat}>
                  <span style={styles.statLabel}>Last Activity:</span>
                  <span style={styles.statValue}>{formatLastActivity(lastActivity)}</span>
                </div>

                {isInactive && <div style={styles.warning}>No drill assignments yet</div>}
              </div>
            );
          }
        )}
      </div>
    </div>
  );
};

export default TeamAggregationStats;
